import { useLevels } from "./levels";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router";
import { ChevronDown, Copy, LayoutDashboard, MonitorSmartphone, Plus } from "lucide-react";
import { clsx } from "clsx";
import { usePopover } from "../ui";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { createDashboard, dashboardTitle, duplicateDashboard, listDashboards } from "../dashboards/model";
import { atLeast } from "./settings";
import { useDevice } from "./device";

const navLink =
  "rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100";

/** "Dashboards" menu (spec §9.7): list, open, and "open on this device" (I-7). Edit/duplicate/delete: M4. */
export function DashboardsMenu({
  core,
  variant = "bar",
}: {
  core: DocHandle | null;
  variant?: "bar" | "tab";
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dashboards = useDocSelector(core, (doc) => listDashboards(doc)) ?? [];
  const device = useDevice((s) => s.dashboard);
  const setDashboard = useDevice((s) => s.setDashboard);
  const { open, toggle, setOpen, ref } = usePopover<HTMLDivElement>();
  const level = useLevels().settings;
  const canEdit = core && !core.readOnly && atLeast(level, "standard");
  if (!dashboards.length) return null;
  return (
    <div ref={ref} className={clsx("relative", variant === "tab" && "flex min-w-0 flex-1")}>
      <button
        type="button"
        className={
          variant === "tab"
            ? "flex min-h-14 w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-sm px-0.5 text-[11px] tracking-[-0.01em] text-neutral-500 hover:text-neutral-900 aria-expanded:text-accent dark:hover:text-neutral-100"
            : clsx(navLink, "inline-flex items-center gap-1")
        }
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        data-testid="dashboards-menu"
      >
        {variant === "tab" ? (
          <>
            <LayoutDashboard size={20} aria-hidden />
            <span className="max-w-full truncate">{t("dashboards.menu")}</span>
          </>
        ) : (
          <>
            {t("dashboards.menu")} <ChevronDown size={13} aria-hidden />
          </>
        )}
      </button>
      {open && (
        <ul
          role="menu"
          className={clsx(
            "absolute z-40 w-64 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700",
            variant === "tab" ? "bottom-full left-0 mb-1" : "left-0 top-9",
          )}
        >
          {dashboards.map((d) => (
            <li key={d.id} className="flex items-center">
              <NavLink
                role="menuitem"
                to={`/d/${d.slug}`}
                onClick={() => setOpen(false)}
                className="min-w-0 flex-1 truncate rounded-sm px-2 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                {dashboardTitle(t, d)}
              </NavLink>
              <button
                type="button"
                role="menuitem"
                title={t("dashboards.openHere")}
                aria-label={t("dashboards.openHere")}
                aria-pressed={device === d.slug}
                data-testid="dashboard-open-here"
                data-slug={d.slug}
                className={clsx(
                  "grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800",
                  device === d.slug && "text-accent",
                )}
                onClick={() => {
                  setDashboard(d.slug);
                  setOpen(false);
                  navigate(`/d/${d.slug}`);
                }}
              >
                <MonitorSmartphone size={14} aria-hidden />
              </button>
              {canEdit && (
                <button
                  type="button"
                  role="menuitem"
                  title={t("dashboards.duplicate")}
                  aria-label={t("dashboards.duplicate")}
                  className="grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                  onClick={() => {
                    const copy = duplicateDashboard(
                      core.doc,
                      d.id,
                      t("dashboards.copyOf", { title: d.title }),
                    );
                    setOpen(false);
                    navigate(`/d/${copy.slug}`);
                  }}
                >
                  <Copy size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
          {canEdit && (
            <li className="mt-1 border-t border-neutral-200 pt-1 dark:border-neutral-800">
              <button
                type="button"
                role="menuitem"
                data-testid="dashboard-new"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-accent hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => {
                  const d = createDashboard(core.doc, { title: t("dashboards.newTitle") });
                  setOpen(false);
                  navigate(`/d/${d.slug}`);
                }}
              >
                <Plus size={14} aria-hidden /> {t("dashboards.new")}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
